/**
 * Generador de historia clínica usando jsPDF
 * con un "padding" de 50 puntos (MARGIN)
 * Usamos una imagen PNG como fondo (plantilla_historia-1.png)
 */
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

export async function generarYDescargarHistoriaClinica(
  historia,
  paciente,
  diagnosticos = [],
  recetas = [],
  doctorNombre = "Doctor"
) {
  try {
    // Compatibilidad con versiones que pasaban plantilla como 5º argumento
    let plantillaArg = null;
    if (arguments.length === 6) {
      plantillaArg = arguments[4];
      doctorNombre = arguments[5];
      // Si se pasó por argumento y aún no existe en historia, lo guardamos
      if (plantillaArg && !historia.plantilla) {
        historia.plantilla = plantillaArg;
      }
    }
    // Aquí definimos la plantilla que de verdad usaremos (si viene dentro de historia)
    const plantilla = historia.plantilla;

    // Tamaño carta en points (aprox 612 x 792)
    const pageWidth = 612;
    const pageHeight = 792;

    // Definimos un margen de 50 puntos en todos los bordes
    const MARGIN = 100;

    // Creamos el documento jsPDF
    const doc = new jsPDF({
      orientation: "p",
      unit: "pt",
      format: [pageWidth, pageHeight],
    });

    // Posición vertical actual (arrancamos en el margen superior)
    let yPos = MARGIN;

    // Función para "inicializar" la PRIMERA página (jsPDF ya te da una página)
    // Solo agregamos la imagen de fondo y ponemos yPos = MARGIN
    const initPrimeraPagina = () => {
      doc.addImage(
        "/data/plantilla_historia-1.png",
        "PNG",
        0,
        0,
        pageWidth,
        pageHeight
      );
      yPos = MARGIN;
    };

    // Función para añadir una NUEVA página
    const nuevaPagina = () => {
      doc.addPage();
      doc.addImage(
        "/data/plantilla_historia-1.png",
        "PNG",
        0,
        0,
        pageWidth,
        pageHeight
      );
      yPos = MARGIN; // Iniciar en el margen superior
    };

    // Verifica si queda espacio suficiente en la página
    // de lo contrario, crea una página nueva
    const checkNewPage = (minSpace = 100) => {
      if (yPos + minSpace > pageHeight - MARGIN) {
        nuevaPagina();
      }
    };

    // Ajustes de estilo para subtítulos
    const setSubheader = () => {
      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.setTextColor("#337ab7");
    };

    // Ajustes de estilo para texto normal
    const setNormalText = () => {
      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.setTextColor("#000000");
    };

    // Función para dibujar un "título de sección" con línea debajo
    const addSectionTitle = (titulo) => {
      setSubheader();
      yPos += 30; // Dejamos un espacio vertical antes
      // Dibujamos el texto a la izquierda (MARGIN + 20 para no quedar pegado)
      doc.text(titulo, MARGIN + 20, yPos);

      // Línea debajo del título (entre márgenes)
      doc.setLineWidth(1);
      doc.setDrawColor(200, 200, 200);
      doc.line(MARGIN + 20, yPos + 5, pageWidth - (MARGIN + 20), yPos + 5);

      yPos += 30; // Bajamos un poco más
      setNormalText();
    };

    // Función genérica para crear una tabla con manejo de errores
    const createTable = (startY, headRow, bodyRows, tableTitle = null, tableOptions = {}) => {
      try {
        const initialY = startY;

        // Opciones por defecto
        const options = {
          startY: startY,
          head: headRow ? [headRow] : undefined,
          body: bodyRows,
          theme: "grid",
          margin: { left: MARGIN + 20, right: MARGIN + 20 },
          styles: {
            overflow: "linebreak",
            cellPadding: 5,
            font: "helvetica",
            fontSize: 10
          },
          headStyles: {
            fillColor: [129, 120, 183],
            textColor: [255, 255, 255],
            fontStyle: "bold"
          },
          ...tableOptions
        };

        // Si hay título para la tabla, lo mostramos antes
        if (tableTitle) {
          doc.setFont("helvetica", "bold");
          doc.setFontSize(12);
          doc.setTextColor("#000000");
          doc.text(tableTitle, MARGIN + 20, initialY - 10);
        }

        const result = autoTable(doc, options);

        if (result && typeof result.finalY === 'number') {
          const bottom = result.finalY;      // fin real de la tabla
          const GUARD = 25;                 // 25 pt ≈ 8 mm de colchón universal
          return bottom + GUARD;
        } else {
          // Fallback si no hay finalY
          const estimatedRowHeight = 20;
          const rowCount = bodyRows.length + (headRow ? 1 : 0);
          return initialY + (rowCount * estimatedRowHeight) + 10;
        }
      } catch (error) {
        console.error("Error al generar tabla:", error);
        return startY + 20; // Valor fallback
      }
    };

    // Iniciamos la primera página con la imagen de fondo
    initPrimeraPagina();

    // 1. Título principal
    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.setTextColor("#000080");

    // Subimos 10 puntos desde el MARGIN, por ejemplo
    // (ya tenemos yPos=MARGIN, sumamos 10)
    yPos += 10;
    doc.text("HISTORIA CLÍNICA OFTALMOLÓGICA", pageWidth / 2, yPos, {
      align: "center",
    });
    yPos += 25;
    const valoresMap = historia.valores || {}; // es un objeto { campoId: valor, … }

    // 2) Encuentra los IDs de los campos “Edad” y “Fecha de nacimiento” en la plantilla
    let edadCampoId = null;
    let fechaNacimientoCampoId = null;
    if (plantilla && plantilla.secciones) {
      plantilla.secciones.forEach(seccion => {
        seccion.campos.forEach(campo => {
          const nombre = campo.nombre?.trim().toLowerCase();
          if (nombre === 'edad') edadCampoId = campo.id;
          if (nombre === 'fecha de nacimiento') fechaNacimientoCampoId = campo.id;
        });
      });
    }

    // 3) Extrae los valores de la plantilla (cae en null si no existe)
    const edadPlantilla = edadCampoId ? valoresMap[edadCampoId] : null;
    const fechaNacPlantilla = fechaNacimientoCampoId
      ? valoresMap[fechaNacimientoCampoId]
      : null;
    // 2. Información del paciente
    addSectionTitle("INFORMACIÓN DEL PACIENTE");

    const infoItems = [
      { label: 'Nombre', value: `${paciente?.nombre || ''} ${paciente?.apellido || ''}` },
      { label: 'Cédula', value: paciente?.cedula || 'N/A' },
      {
        label: 'Edad', value: edadPlantilla
          ? `${edadPlantilla}`
          : (paciente?.edad != null ? paciente.edad : 'N/A')
      },
      {
        label: 'Fecha de nacimiento', value: fechaNacPlantilla
          // formatea la fecha de la plantilla
          ? new Date(fechaNacPlantilla).toLocaleDateString('es-ES')
          // o la de paciente (si la tuvieras)
          : (paciente?.fechaNacimiento
            ? new Date(paciente.fechaNacimiento).toLocaleDateString('es-ES')
            : 'N/A'
          )
      },
      { label: 'Teléfono', value: paciente?.telefono || 'N/A' },
      { label: 'Correo electrónico', value: paciente?.email || 'N/A' },
    ];

    // Convertimos infoItems a formato para autoTable
    const tableData = infoItems.map(item => [item.label, item.value]);

    // Verificamos si hay espacio suficiente para la tabla
    checkNewPage(150); // Estimación aproximada de altura de tabla

    // Utilizamos la función createTable para generar la tabla
    yPos = createTable(
      yPos,
      ["Datos del paciente", "Información"],
      tableData
    );

    // 3. Datos clínicos - Mejorado para usar la estructura de plantilla
    if (historia && historia.plantilla && historia.plantilla.secciones) {
      // Procesamos cada sección de la plantilla, excepto "Datos del paciente"
      historia.plantilla.secciones
        .filter(seccion => seccion.nombre.toLowerCase() !== 'datos del paciente') // Filtramos esta sección para evitar duplicados
        .forEach((seccion) => {
          // Solo mostramos secciones con campos
          if (seccion.campos && seccion.campos.length > 0) {
            addSectionTitle(seccion.nombre.toUpperCase());

            // Preparamos los datos de la tabla por sección
            const camposData = [];

            seccion.campos.forEach((campo) => {
              // Obtenemos el valor del campo (de historia.valores)
              let valorCampo = "N/A"; // Valor por defecto

              // Primero intentamos obtener el valor desde historia.valores
              if (historia.valores && typeof historia.valores === 'object') {
                // Si es un objeto simple (clave-valor)
                if (historia.valores[campo.id] !== undefined) {
                  valorCampo = historia.valores[campo.id];
                }
                // Si es un array de valores
                else if (Array.isArray(historia.valores)) {
                  const valorEncontrado = historia.valores.find(v => v.campoId === campo.id);
                  if (valorEncontrado) {
                    valorCampo = valorEncontrado.valor || valorEncontrado;
                  }
                }
              }

              // Formatear según el tipo de campo
              switch (campo.tipo) {
                case 'FECHA':
                  try {
                    if (valorCampo && valorCampo !== 'N/A') {
                      const fecha = new Date(valorCampo);
                      if (!isNaN(fecha)) {
                        valorCampo = fecha.toLocaleDateString('es-ES');
                      }
                    }
                  } catch (e) {
                    console.error("Error al formatear fecha:", e);
                  }
                  break;

                case 'IMAGEN':
                case 'ARCHIVO':
                  // Si es imagen o archivo, indicamos que es un archivo
                  valorCampo = valorCampo === 'N/A' ? 'N/A' : '[Archivo adjunto]';
                  break;

                default:
                  // Para otros tipos, mantenemos el valor como texto
                  valorCampo = String(valorCampo);
              }

              camposData.push([campo.nombre, valorCampo]);
            });

            // Solo creamos la tabla si hay datos
            if (camposData.length > 0) {
              checkNewPage(camposData.length * 25 + 50); // Estimamos espacio necesario

              yPos = createTable(
                yPos,
                null, // Sin encabezado
                camposData,
                null, // Sin título de tabla
                {
                  tableLineColor: [180, 180, 200],
                  tableLineWidth: 0.5,
                  styles: {
                    overflow: "linebreak",
                    cellPadding: 4,
                    font: "helvetica",
                    fontSize: 9,
                    halign: "left"
                  },
                  columnStyles: {
                    0: { fontStyle: 'bold', cellWidth: 180 },
                    1: { cellWidth: 'auto' }
                  }
                }
              );

              // Espacio entre secciones
              yPos += 10;
            }
          }
        });
    } else if (historia?.valores && Object.keys(historia.valores).length > 0) {
      // Fallback para versión anterior: mostrar valores simples
      addSectionTitle("DATOS CLÍNICOS");

      // Preparamos los datos para la tabla
      const camposData = [];

      Object.entries(historia.valores).forEach(([key, valor]) => {
        camposData.push([key, String(valor)]);
      });

      // Crear la tabla de datos clínicos
      checkNewPage(camposData.length * 20 + 40);

      yPos = createTable(
        yPos,
        null, // Sin encabezado
        camposData,
        null, // Sin título de tabla
        {
          tableLineColor: [180, 180, 200],
          tableLineWidth: 0.5,
          styles: {
            overflow: "linebreak",
            cellPadding: 4,
            font: "helvetica",
            fontSize: 9
          },
          columnStyles: {
            0: { fontStyle: 'bold', cellWidth: 180 },
            1: { cellWidth: 'auto' }
          }
        }
      );
    }

    // 4. Diagnósticos
    if (diagnosticos && diagnosticos.length > 0) {
      addSectionTitle("DIAGNÓSTICOS");

      diagnosticos
        .sort((a, b) => {
          const fechaA = new Date(a.fecha || a.createdAt).getTime();
          const fechaB = new Date(b.fecha || b.createdAt).getTime();
          return fechaB - fechaA; // más recientes primero
        })
        .forEach((diag, i) => {
          checkNewPage(150); // Espacio mínimo para diagnóstico

          const fecha = new Date(
            diag.fecha || diag.createdAt
          ).toLocaleDateString("es-ES");

          // Título del diagnóstico
          const diagTitle = `Diagnóstico #${i + 1} - ${fecha}`;

          // Creamos la tabla con los datos del diagnóstico
          const diagData = [];

          // Descripción
          diagData.push(["Descripción", diag.descripcion || "N/A"]);

          // Código CIE-10 (si existe)
          if (diag.codigoCIE10) {
            diagData.push(["Código CIE-10", diag.codigoCIE10]);
          }

          // Detalles (si existen)
          if (diag.detalles) {
            diagData.push(["Detalles", diag.detalles]);
          }

          // Crear tabla de diagnóstico
          yPos = createTable(
            yPos,
            null, // Sin encabezado
            diagData,
            diagTitle,
            {
              tableLineColor: [100, 100, 200],
              tableLineWidth: 0.5,
              styles: {
                overflow: "linebreak",
                cellPadding: 5,
                font: "helvetica",
                fontSize: 10,
                halign: "left"
              },
              columnStyles: {
                0: { fontStyle: 'bold', cellWidth: 150 },
                1: { cellWidth: 'auto' }
              }
            }
          );

          // Espacio entre diagnósticos
          yPos += 15;
        });
    }

    // 5. Recetas médicas
    if (recetas && recetas.length > 0) {
      addSectionTitle("RECETAS MÉDICAS");

      recetas
        .sort((a, b) => {
          const fa = new Date(a.fecha || a.fechaInicio || a.createdAt).getTime();
          const fb = new Date(b.fecha || b.fechaInicio || b.createdAt).getTime();
          return fb - fa;
        })
        .forEach((rec, i) => {
          checkNewPage(200); // Necesitamos más espacio para la tabla completa

          const fecha = new Date(
            rec.fecha || rec.fechaInicio || rec.createdAt
          ).toLocaleDateString("es-ES");

          // Título de la receta
          const recetaTitle = `Receta #${i + 1} - ${fecha}`;

          // 1. Tabla de información general de la receta
          const infoRecetaRows = [];

          if (rec.indicaciones) {
            infoRecetaRows.push(["Indicaciones generales", rec.indicaciones]);
          }

          if (rec.proximaCita) {
            infoRecetaRows.push([
              "Próxima cita",
              new Date(rec.proximaCita).toLocaleDateString("es-ES")
            ]);
          }

          // Diagnóstico relacionado (si existe)
          if (rec.diagnostico && rec.diagnostico.descripcion) {
            infoRecetaRows.push(["Diagnóstico", rec.diagnostico.descripcion]);
          }

          // Solo creamos esta tabla si hay contenido
          if (infoRecetaRows.length > 0) {
            yPos = createTable(
              yPos,
              null, // Sin encabezado
              infoRecetaRows,
              recetaTitle, // Título de la tabla
              {
                tableLineColor: [100, 100, 200],
                tableLineWidth: 0.5,
                styles: {
                  overflow: "linebreak",
                  cellPadding: 5,
                  font: "helvetica",
                  fontSize: 10,
                  halign: "left"
                },
                columnStyles: {
                  0: { fontStyle: 'bold', cellWidth: 150 },
                  1: { cellWidth: 'auto' }
                }
              }
            );
          } else {
            // Si no hay info general, solo mostramos el título
            doc.setFont("helvetica", "bold");
            doc.setFontSize(12);
            doc.text(recetaTitle, MARGIN + 20, yPos);
            yPos += 20;
          }

          // 2. Tabla de medicamentos
          const meds = rec.medicamentos || [rec];

          if (meds.length > 0) {
            // Si hay suficiente información, dejamos un subtítulo
            if (infoRecetaRows.length > 0) {
              yPos += 8;
              checkNewPage(50);
              doc.setFont("helvetica", "bold");
              doc.text("Medicamentos:", MARGIN + 20, yPos);
              yPos += 25;
            }

            // Para cada medicamento, creamos una tabla
            meds.forEach((med, idx) => {
              checkNewPage(150);

              const medTitle = `${idx + 1}. ${med.nombre || "N/A"}`;
              const medRows = [];

              if (med.dosis) {
                medRows.push(["Dosis", med.dosis]);
              }

              if (med.frecuencia) {
                medRows.push(["Frecuencia", med.frecuencia]);
              }

              if (med.duracion !== undefined && med.duracion !== null) {
                medRows.push(["Duración", `${med.duracion} días`]);
              }

              if (med.indicaciones) {
                medRows.push(["Indicaciones específicas", med.indicaciones]);
              }

              // Si no hay detalles, al menos mostramos una fila con "Sin detalles"
              if (medRows.length === 0) {
                medRows.push(["Detalles", "No se especificaron detalles"]);
              }

              yPos = createTable(
                yPos,
                null, // Sin encabezado
                medRows,
                medTitle, // Título del medicamento
                {
                  tableLineColor: [180, 180, 200],
                  tableLineWidth: 0.5,
                  styles: {
                    overflow: "linebreak",
                    cellPadding: 4,
                    font: "helvetica",
                    fontSize: 9,
                    halign: "left"
                  },
                  columnStyles: {
                    0: { fontStyle: 'bold', cellWidth: 150 },
                    1: { cellWidth: 'auto' }
                  }
                }
              );

              // Pequeño espacio entre medicamentos
              yPos += 5;
            });
          }

          // Espacio entre recetas
          yPos += 15;
        });
    }

    // 6. Exámenes complementarios (si existen en la estructura)
    if (plantilla && plantilla.secciones) {
      const seccionExamenes = plantilla.secciones.find(sec =>
        sec.nombre.toLowerCase().includes('examen') &&
        sec.nombre.toLowerCase().includes('complementa')
      );

      if (seccionExamenes && seccionExamenes.campos && seccionExamenes.campos.length > 0) {
        // Ya se ha procesado en la sección de datos clínicos,
        // por lo que no necesitamos repetirlo aquí
      }
    }

    // 7. Firma del doctor. Dejamos algo de espacio final
    yPos += 30;
    checkNewPage(50);
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(1);

    // Línea de firma en la derecha, pero sin salirse de los márgenes
    const firmaXInicio = pageWidth - (MARGIN + 200); // algo a la derecha
    const firmaXFin = pageWidth - (MARGIN + 50);
    doc.line(firmaXInicio, yPos, firmaXFin, yPos);
    yPos += 15;

    // Nombre del doctor (centrado en la línea de firma)
    const textoNombre = doctorNombre+ ' Rios';
    const anchoNombre = doc.getTextWidth(textoNombre);
    const posXNombre = firmaXInicio + ((firmaXFin - firmaXInicio) - anchoNombre) / 2;

    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.text(textoNombre, posXNombre, yPos);
    yPos += 12;

    // "Médico Tratante" debajo
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    const textoTratante = "Cirujana Oftalmóloga";
    const anchoTratante = doc.getTextWidth(textoTratante);
    const posXTratante = firmaXInicio + ((firmaXFin - firmaXInicio) - anchoTratante) / 2;

    doc.text(textoTratante, posXTratante, yPos);

    // Fecha y hora de generación
    yPos += 25;
    const fechaGeneracion = new Date().toLocaleString('es-ES');
    doc.setFontSize(8);
    doc.setFont("helvetica", "italic");
    doc.setTextColor(100, 100, 100);
    doc.text(`Documento generado el: ${fechaGeneracion}`, MARGIN + 20, yPos);

    // 8. Descargar el PDF
    const nombreArchivo = `Historia_Clinica_${paciente?.cedula || "Paciente"
      }_${new Date().toISOString().split("T")[0]}.pdf`;
    doc.save(nombreArchivo);

    return true;
  } catch (error) {
    console.error("Error al generar PDF con jsPDF:", error);
    return false;
  }
}